import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Box, Chip, Grid, List, ListItemButton, ListItemText, Stack, Typography } from '@mui/material';
import type { CloneDetail, Job } from '@api/api';
import { endpoints, qk } from '../../api/endpoints';
import { EmptyState, ErrorState, RelTime, SectionCard, SkeletonRows } from '../../components/common';
import { jobLabel, JobView, jobDuration } from '../../components/JobView';
import { JOB_CHIP } from '../../lib/status';
import { isActiveJob } from '../../api/useJobStream';

export function JobList({ jobs, selected, onSelect }: { jobs: Job[]; selected: number | null; onSelect: (id: number) => void }) {
  return (
    <List disablePadding dense>
      {jobs.map((j) => (
        <ListItemButton key={j.id} selected={j.id === selected} onClick={() => onSelect(j.id)} sx={{ borderRadius: 1, mb: 0.25 }}>
          <ListItemText
            primary={
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }} noWrap>
                  {jobLabel(j.type)} <Typography component="span" variant="caption" color="text.secondary">#{j.id}</Typography>
                </Typography>
                <Chip size="small" color={JOB_CHIP[j.status]} label={j.status} variant={isActiveJob(j) ? 'filled' : 'outlined'} />
              </Stack>
            }
            secondary={
              <span>
                <RelTime iso={j.createdAt} /> · {jobDuration(j)}
                {j.createdBy ? ` · ${j.createdBy}` : ''}
              </span>
            }
            secondaryTypographyProps={{ component: 'span', noWrap: true, display: 'block' }}
          />
        </ListItemButton>
      ))}
    </List>
  );
}

export function JobsTab({ clone: c }: { clone: CloneDetail }) {
  const [params, setParams] = useSearchParams();
  const jobs = useQuery({ queryKey: qk.cloneJobs(c.id), queryFn: () => endpoints.cloneJobs(c.id), refetchInterval: (q) => (q.state.data?.some(isActiveJob) ? 5000 : 30_000) });
  const selected = Number(params.get('job')) || jobs.data?.[0]?.id || null;

  if (jobs.isLoading) return <SkeletonRows rows={5} />;
  if (jobs.isError) return <ErrorState error={jobs.error} onRetry={() => void jobs.refetch()} />;
  if (!jobs.data?.length) return <EmptyState title="No jobs yet" text="Provision, deploy, restart and backups show up here with their full log." />;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4} lg={3}>
        <SectionCard title="Jobs">
          <Box sx={{ maxHeight: { md: 'calc(100vh - 300px)' }, overflowY: 'auto', mx: -1 }}>
            <JobList jobs={jobs.data} selected={selected} onSelect={(id) => setParams({ job: String(id) }, { replace: true })} />
          </Box>
        </SectionCard>
      </Grid>
      <Grid item xs={12} md={8} lg={9}>
        <SectionCard>{selected ? <JobView key={selected} id={selected} /> : <EmptyState title="Pick a job" />}</SectionCard>
      </Grid>
    </Grid>
  );
}
